/**
 * Activity payload compression utilities
 * Compresses all keys in activity payloads to reduce bandwidth usage
 */

// Activity type enum
export enum ActivityType {
  VIEW = 'v',
  ACTIVITY = 'a', 
  SEGMENT = 's',
  SCT = 'c', // current time tracking
}

// Video action enum
export enum VideoAction {
  PLAY = 'p',
  PAUSE = 'u', // 'pause' -> 'u' (unique letter)
  END = 'e',
}

// Main payload structure mapping
const MAIN_PAYLOAD_MAP = {
  type: 't',
  resource: 'r',
  data: 'd',
} as const;

// Data object key mappings for different activity types
const DATA_KEY_MAPS = {
  // Video activity data keys
  [ActivityType.ACTIVITY]: {
    action: 'a',
    currentTime: 'ct',
  },
  // Video segment data keys  
  [ActivityType.SEGMENT]: {
    start: 's',
    end: 'e', 
    duration: 'd',
    isComplete: 'ic',
    reason: 'r',
    timestamp: 'ts',
    quality: 'q',
    speed: 'sp',
  },
  // Current time tracking data keys
  [ActivityType.SCT]: {
    currentTime: 'ct',
  },
  // Resource view data keys (currently empty but future-proof)
  [ActivityType.VIEW]: {},
} as Record<string, Record<string, string>>;

// Reverse mappings for expansion
const REVERSE_MAIN_PAYLOAD_MAP = Object.fromEntries(
  Object.entries(MAIN_PAYLOAD_MAP).map(([key, value]) => [value, key])
) as Record<string, keyof typeof MAIN_PAYLOAD_MAP>;

// Reverse activity type mapping
const REVERSE_ACTIVITY_TYPE_MAP = {
  [ActivityType.VIEW]: 'view',
  [ActivityType.ACTIVITY]: 'activity', 
  [ActivityType.SEGMENT]: 'segment',
  [ActivityType.SCT]: 'sct',
} as const;

// Reverse video action mapping
const REVERSE_VIDEO_ACTION_MAP = {
  [VideoAction.PLAY]: 'play',
  [VideoAction.PAUSE]: 'pause',
  [VideoAction.END]: 'end',
} as const;

const REVERSE_DATA_KEY_MAPS = Object.fromEntries(
  Object.entries(DATA_KEY_MAPS).map(([type, keys]) => [
    type,
    Object.fromEntries(
      Object.entries(keys).map(([key, value]) => [value, key])
    )
  ])
) as Record<string, Record<string, string>>;

type CompressedPayload = {
  t: string;
  r: string | number;
  d: Record<string, any>;
};

type ExpandedPayload = {
  type: string;
  resource: string | number;
  data: Record<string, any>;
};

/**
 * Compress activity payload by shortening all keys
 */
export function compressActivityPayload(payload: ExpandedPayload): CompressedPayload {
  const { type, resource, data } = payload;
  
  // Map activity type to compressed version
  let compressedType: string;
  switch (type) {
    case 'view':
      compressedType = ActivityType.VIEW;
      break;
    case 'activity':
      compressedType = ActivityType.ACTIVITY;
      break;
    case 'segment':
      compressedType = ActivityType.SEGMENT;
      break;
    case 'sct':
      compressedType = ActivityType.SCT;
      break;
    default:
      compressedType = type; // fallback for unknown types
  }
  
  // Compress main payload structure
  const compressed: CompressedPayload = {
    t: compressedType,
    r: resource,
    d: {},
  };

  // Compress data object keys based on activity type
  const dataKeyMap = DATA_KEY_MAPS[compressedType];
  if (dataKeyMap && data) {
    Object.entries(data).forEach(([key, value]) => {
      const compressedKey = dataKeyMap[key] || key;
      let compressedValue = value;
      
      // Special handling for video actions
      if (key === 'action' && compressedType === ActivityType.ACTIVITY) {
        switch (value) {
          case 'play':
            compressedValue = VideoAction.PLAY;
            break;
          case 'pause':
            compressedValue = VideoAction.PAUSE;
            break;
          case 'end':
            compressedValue = VideoAction.END;
            break;
        }
      }
      
      compressed.d[compressedKey] = compressedValue;
    });
  }

  return compressed;
}

/**
 * Expand compressed activity payload back to original structure
 */
export function expandActivityPayload(compressed: CompressedPayload): ExpandedPayload {
  const { t, r, d } = compressed;
  
  // Map compressed activity type back to original
  let expandedType: string;
  switch (t) {
    case ActivityType.VIEW:
      expandedType = 'view';
      break;
    case ActivityType.ACTIVITY:
      expandedType = 'activity';
      break;
    case ActivityType.SEGMENT:
      expandedType = 'segment';
      break;
    case ActivityType.SCT:
      expandedType = 'sct';
      break;
    default:
      expandedType = t; // fallback for unknown types
  }
  
  // Expand main payload structure
  const expanded: ExpandedPayload = {
    type: expandedType,
    resource: r,
    data: {},
  };

  // Expand data object keys based on activity type
  const reverseDataKeyMap = REVERSE_DATA_KEY_MAPS[t];
  if (reverseDataKeyMap && d) {
    Object.entries(d).forEach(([key, value]) => {
      const expandedKey = reverseDataKeyMap[key] || key;
      let expandedValue = value;
      
      // Special handling for video actions
      if (key === 'a' && t === ActivityType.ACTIVITY) {
        switch (value) {
          case VideoAction.PLAY:
            expandedValue = 'play';
            break;
          case VideoAction.PAUSE:
            expandedValue = 'pause';
            break;
          case VideoAction.END:
            expandedValue = 'end';
            break;
        }
      }
      
      expanded.data[expandedKey] = expandedValue;
    });
  }

  return expanded;
}

/**
 * Get compression ratio for debugging/monitoring
 */
export function getCompressionRatio(original: ExpandedPayload, compressed: CompressedPayload): number {
  const originalSize = JSON.stringify(original).length;
  const compressedSize = JSON.stringify(compressed).length;
  return Math.round(((originalSize - compressedSize) / originalSize) * 100);
}
